"use client";

import { nanoid } from "nanoid";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { useAiChatUiContext } from "@/lib/ai/ai-chat-ui-context";
import { AI_CHAT_MODEL_STORAGE_KEY } from "@/lib/ai/constants";
import { normalizeModelId } from "@/lib/ai/models";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { StorageManager } from "@/lib/storage/local-storage";
import { ChatThread } from "../../_components/ai/thread/chat-thread";
import {
  aiChatPath,
  serializeAiChatSearchParams,
} from "../_search-params/ai-chat-search-params";

type Props = {
  workspaceId: string;
  lang: string;
};

const CHAT_SEED_VERSION = 1;
const CHAT_SEED_MAX_AGE_MS = 1000 * 60 * 10;

type SummaryChatSeed = {
  version: number;
  summary: string;
  modelId: string | null;
  createdAt: string;
};

export function CreateChatRedirector({ workspaceId, lang }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { currentModel } = useAiChatUiContext();
  const seedToken = searchParams?.get("seed");

  const initialMessages = useMemo<AgentUIMessage[]>(() => {
    if (!seedToken) {
      return [];
    }

    const seedKey = `amethyst:ai:summary-seed:v${CHAT_SEED_VERSION}:${seedToken}`;
    const seed = StorageManager.getJSON<SummaryChatSeed>(seedKey);
    StorageManager.removeItem(seedKey);

    if (!seed || seed.version !== CHAT_SEED_VERSION || !seed.summary) {
      return [];
    }

    const createdAt = new Date(seed.createdAt).getTime();
    if (
      !Number.isFinite(createdAt) ||
      Date.now() - createdAt > CHAT_SEED_MAX_AGE_MS
    ) {
      return [];
    }

    const messageId = `seed-assistant-${seedToken}-${nanoid()}`;
    return [
      {
        id: messageId,
        role: "assistant",
        metadata: {
          clientMessageId: messageId,
          modelId: seed.modelId ?? undefined,
        },
        parts: [{ type: "text", text: seed.summary }],
      },
    ];
  }, [seedToken]);

  return (
    <ChatThread
      workspaceId={workspaceId}
      initialMessages={initialMessages}
      compactStarterWhenInactive={false}
      onChatCreated={(chatId) => {
        const redirectModel =
          normalizeModelId(StorageManager.getItem(AI_CHAT_MODEL_STORAGE_KEY)) ??
          currentModel;
        router.push(
          serializeAiChatSearchParams(
            aiChatPath(lang, workspaceId, chatId),
            searchParams,
            typeof window === "undefined" ? "" : window.location.hash,
            {
              seed: null,
              model: redirectModel,
            },
          ),
        );
      }}
    />
  );
}
